import type { HostingConfig, HostingPayment, HostingPlan } from "@elektr/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input, Select } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

const CURRENCIES = ["UZS", "EUR", "USD"] as const;
type Currency = (typeof CURRENCIES)[number];
const today = () => new Date().toISOString().slice(0, 10);

function useRefresh() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["hosting"] });
}

function CurrencySelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: Currency;
  onChange: (c: Currency) => void;
}) {
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value as Currency)}>
      {CURRENCIES.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </Select>
  );
}

function Actions({ pending, onCancel }: { pending: boolean; onCancel: () => void }) {
  const { t } = useT();
  return (
    <div className="flex justify-end gap-2 pt-2">
      <Button type="button" variant="outline" onClick={onCancel}>
        {t("common.cancel")}
      </Button>
      <Button type="submit" disabled={pending}>
        {t("common.save")}
      </Button>
    </div>
  );
}

/** Provayder, xarajat va keyingi to'lov sanasi. Kalit bu yerda kiritilmaydi — faqat sozlanganligi ko'rsatiladi. */
export function ConfigDialog({
  open,
  onOpenChange,
  config,
  plans,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  config: HostingConfig;
  plans: HostingPlan[];
}) {
  const { t } = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t("server.settings")}>
      {open && <ConfigForm config={config} plans={plans} onClose={() => onOpenChange(false)} />}
    </Dialog>
  );
}

function ConfigForm({
  config,
  plans,
  onClose,
}: {
  config: HostingConfig;
  plans: HostingPlan[];
  onClose: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const refresh = useRefresh();
  const [f, setF] = useState({
    provider: config.provider ?? "manual",
    volume: config.hetzner_volume_id ?? "",
    plan: config.current_plan ? String(config.current_plan) : "",
    server: config.server_monthly ?? "0",
    backup: config.backup_monthly ?? "0",
    currency: (config.cost_currency ?? "UZS") as Currency,
    due: config.next_payment_date ?? "",
  });
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.PATCH("/api/hosting/config/", {
          body: {
            provider: f.provider,
            hetzner_volume_id: f.volume.trim(),
            current_plan: f.plan ? Number(f.plan) : null,
            server_monthly: f.server || "0",
            backup_monthly: f.backup || "0",
            cost_currency: f.currency,
            next_payment_date: f.due || null,
          },
        }),
      ),
    onSuccess: () => {
      refresh();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label={t("server.provider")}>
        {(id) => (
          <Select
            id={id}
            value={f.provider}
            onChange={(e) => setF({ ...f, provider: e.target.value as "hetzner" | "manual" })}
          >
            <option value="hetzner">{t("server.provider_hetzner")}</option>
            <option value="manual">{t("server.provider_manual")}</option>
          </Select>
        )}
      </Field>
      {f.provider === "hetzner" ? (
        <Field
          label={t("server.volume_id")}
          hint={config.api_token_set ? t("server.token_set") : undefined}
          error={config.api_token_set ? null : t("server.token_missing")}
        >
          {(id) => (
            <Input
              id={id}
              value={f.volume}
              onChange={(e) => setF({ ...f, volume: e.target.value })}
              inputMode="numeric"
            />
          )}
        </Field>
      ) : (
        <Field label={t("server.current_plan")}>
          {(id) => (
            <Select id={id} value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>
              <option value="">{t("server.no_plan")}</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.size_gb} GB)
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={t("server.server_monthly")}>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              step="any"
              value={f.server}
              onChange={(e) => setF({ ...f, server: e.target.value })}
            />
          )}
        </Field>
        <Field label={t("server.backup_monthly")}>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              step="any"
              value={f.backup}
              onChange={(e) => setF({ ...f, backup: e.target.value })}
            />
          )}
        </Field>
        <Field label={t("server.currency")}>
          {(id) => (
            <CurrencySelect
              id={id}
              value={f.currency}
              onChange={(currency) => setF({ ...f, currency })}
            />
          )}
        </Field>
      </div>
      <Field label={t("server.next_payment_date")}>
        {(id) => (
          <Input
            id={id}
            type="date"
            value={f.due}
            onChange={(e) => setF({ ...f, due: e.target.value })}
          />
        )}
      </Field>
      <Actions pending={save.isPending} onCancel={onClose} />
    </form>
  );
}

export function PlanDialog({
  plan,
  open,
  onOpenChange,
}: {
  plan: HostingPlan | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={plan ? plan.name : t("server.plan_add")}>
      {open && <PlanForm plan={plan} onClose={() => onOpenChange(false)} />}
    </Dialog>
  );
}

function PlanForm({ plan, onClose }: { plan: HostingPlan | null; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const refresh = useRefresh();
  const [f, setF] = useState({
    name: plan?.name ?? "",
    size: plan ? String(plan.size_gb) : "",
    price: plan?.price ?? "",
    currency: (plan?.currency ?? "UZS") as Currency,
    period: plan?.period ?? "monthly",
    url: plan?.billing_url ?? "",
    checked: plan?.checked_on ?? today(),
  });
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: f.name.trim(),
        size_gb: Number(f.size),
        price: f.price,
        currency: f.currency,
        period: f.period,
        billing_url: f.url.trim(),
        checked_on: f.checked,
      };
      return plan
        ? unwrap(
            await api.PATCH("/api/hosting/plans/{id}/", {
              params: { path: { id: plan.id } },
              body,
            }),
          )
        : unwrap(await api.POST("/api/hosting/plans/", { body }));
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  const valid = f.name.trim() && Number(f.size) >= 1 && Number(f.price) >= 0 && f.price !== "";
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate();
      }}
    >
      <Field label={t("server.plan_name")}>
        {(id) => (
          <Input
            id={id}
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            required
          />
        )}
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("server.plan_size")}>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={1}
              value={f.size}
              onChange={(e) => setF({ ...f, size: e.target.value })}
              required
            />
          )}
        </Field>
        <Field label={t("server.plan_period")}>
          {(id) => (
            <Select
              id={id}
              value={f.period}
              onChange={(e) => setF({ ...f, period: e.target.value as "monthly" | "yearly" })}
            >
              <option value="monthly">{t("server.period_monthly")}</option>
              <option value="yearly">{t("server.period_yearly")}</option>
            </Select>
          )}
        </Field>
        <Field label={t("server.plan_price")}>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              step="any"
              value={f.price}
              onChange={(e) => setF({ ...f, price: e.target.value })}
              required
            />
          )}
        </Field>
        <Field label={t("server.currency")}>
          {(id) => (
            <CurrencySelect
              id={id}
              value={f.currency}
              onChange={(currency) => setF({ ...f, currency })}
            />
          )}
        </Field>
      </div>
      <Field label={t("server.plan_url")}>
        {(id) => (
          <Input
            id={id}
            type="url"
            value={f.url}
            onChange={(e) => setF({ ...f, url: e.target.value })}
            placeholder="https://"
          />
        )}
      </Field>
      <Field label={t("server.plan_checked")}>
        {(id) => (
          <Input
            id={id}
            type="date"
            value={f.checked}
            onChange={(e) => setF({ ...f, checked: e.target.value })}
            required
          />
        )}
      </Field>
      <Actions pending={save.isPending || !valid} onCancel={onClose} />
    </form>
  );
}

export function PaymentDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t("server.payment_add")}>
      {open && <PaymentForm onClose={() => onOpenChange(false)} />}
    </Dialog>
  );
}

function PaymentForm({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const refresh = useRefresh();
  const [f, setF] = useState({
    paid: today(),
    amount: "",
    currency: "UZS" as Currency,
    until: "",
    note: "",
  });
  const save = useMutation({
    mutationFn: async (): Promise<HostingPayment> =>
      unwrap(
        await api.POST("/api/hosting/payments/", {
          body: {
            paid_on: f.paid,
            amount: f.amount,
            currency: f.currency,
            covers_until: f.until,
            note: f.note.trim(),
          },
        }),
      ),
    onSuccess: () => {
      refresh();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  const valid = f.amount !== "" && Number(f.amount) >= 0 && f.until !== "";
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("server.payment_paid_on")}>
          {(id) => (
            <Input
              id={id}
              type="date"
              value={f.paid}
              onChange={(e) => setF({ ...f, paid: e.target.value })}
              required
            />
          )}
        </Field>
        <Field label={t("server.payment_until")}>
          {(id) => (
            <Input
              id={id}
              type="date"
              value={f.until}
              onChange={(e) => setF({ ...f, until: e.target.value })}
              required
            />
          )}
        </Field>
        <Field label={t("server.payment_amount")}>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              step="any"
              value={f.amount}
              onChange={(e) => setF({ ...f, amount: e.target.value })}
              required
            />
          )}
        </Field>
        <Field label={t("server.currency")}>
          {(id) => (
            <CurrencySelect
              id={id}
              value={f.currency}
              onChange={(currency) => setF({ ...f, currency })}
            />
          )}
        </Field>
      </div>
      <Field label={t("server.payment_note")}>
        {(id) => (
          <Input
            id={id}
            value={f.note}
            maxLength={300}
            onChange={(e) => setF({ ...f, note: e.target.value })}
          />
        )}
      </Field>
      <Actions pending={save.isPending || !valid} onCancel={onClose} />
    </form>
  );
}
